/**
 * mediaPath — recover a media uuid from an upload path.
 *
 * WHY THIS EXISTS
 * ---------------
 * The CMS stores images as `<uuid>.<ext>` under `/uploads/<yyyy-mm>/`, and the
 * responsive ladder adds `<uuid>.<rung>.webp` beside it:
 *
 *   /uploads/2026-10/fae4984b-….png                  ← original
 *   /uploads/2026-10/fae4984b-….thumb.webp           ← rung
 *   /uploads/2026-10/fae4984b-….small.webp           ← rung
 *
 * A few surfaces only ever see the URL — a Markdown body image, a lecture
 * photo path, a card cover that was resolved to a string one layer up — and
 * they still need the media row (its native size, its alt, its srcset). The
 * uuid is the key into `cmsMediaIndex`, and the filename stem IS the uuid, so
 * it can be recovered from the path alone.
 *
 * WHY THE STEM IS VALIDATED
 * --------------------------
 * Not every image on the site comes from the media library: `/media/…`,
 * `/products/smart-cindy/…` and `/about-yada-core-values.svg` are static
 * build assets. Recovering "the stem" from `/products/hero-image.png` would
 * yield `hero-image`, and looking that up in the media index would be a
 * guaranteed miss dressed up as a hit. The uuid format is checked instead,
 * so a non-library path yields `null` and the caller keeps its current
 * behaviour rather than silently dropping the image.
 */

/** A uuid in its canonical 8-4-4-4-12 hex form. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * The uuid of the media row an upload path points at, or null.
 *
 * The stem is `path.basename(url).split('.')[0]`, which is the uuid for both
 * the original (`<uuid>.png`) and every rung (`<uuid>.medium.webp`) — the
 * first dot ends the uuid. A query string or a fragment is stripped first,
 * because a cache-busting `?v=2` would otherwise become part of the
 * filename.
 */
export function uuidFromUploadPath(url: string | null | undefined): string | null {
  if (!url) return null
  // Drop the query/fragment — `?v=2` and `#x` are not part of the filename.
  const withoutQuery = url.split(/[?#]/)[0] ?? ''
  const segments = withoutQuery.split('/')
  const filename = segments[segments.length - 1] ?? ''
  if (!filename) return null
  const stem = filename.split('.')[0] ?? ''
  if (!UUID_RE.test(stem)) return null
  return stem.toLowerCase()
}
